import React from "react";
import { Reveal } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { BookButton } from "@/components/site/BookButton";
import { Seo } from "@/components/site/Seo";
import { resolveImg } from "@/lib/api";
import { LoopVideo } from "@/components/site/LoopVideo";

const IMG = {
  hero: "https://images.unsplash.com/photo-1585421079919-44c712bdf839?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  video: "/api/media/file/27fdc553-bbad-43f8-9564-b28b1e7d4997",
  poster: "/api/media/file/72d70684-18e0-481d-9611-6d94639cf27a",
};

const SUPPORTS = [
  ["Sleep", "Parents often notice restlessness and difficulty settling. Care supports the calming, restorative side of the nervous system that helps the body wind down."],
  ["Focus & regulation", "A well-regulated nervous system helps children stay calm, attentive and adaptable. Many families seek gentle support for kids who feel constantly 'switched on.'"],
  ["Colic & digestive comfort", "The nerves that supply the gut are part of the same calming system. Gentle care is a common, low-force option families explore for fussy, uncomfortable little ones."],
  ["Posture & 'tech neck'", "Backpacks, screens and growth spurts load young spines early. We help build good movement habits and postural awareness before compensations set in."],
  ["Sports & growing bodies", "Active kids and teen athletes ask a lot of developing joints. Assessment and gentle care support coordinated, resilient movement."],
  ["Milestones & movement", "Crawling, walking and playing are how a child's nervous system builds its map of the body. We support healthy, symmetrical movement as they grow."],
];

export default function Pediatric() {
  return (
    <div>
      <Seo title="Pediatric Chiropractor in Gardner, KS | Gentle Children's Care | Psalm 30:2" description="Gentle, nervous-system-focused pediatric chiropractic in Gardner, Kansas. Supporting sleep, focus, digestion/colic, posture and healthy development for infants, children and teens." />
      <PageHero
        eyebrow="Children & Teens"
        title={<>A gentle start<br />for a growing<br /><span className="italic text-taupe">nervous system.</span></>}
        intro="A child's nervous system is developing at an incredible pace — building the map it will use to move, focus, sleep and self-regulate for life. Our care is extremely gentle, and always centered on your child's comfort."
        image={IMG.hero}
      />

      {/* Why */}
      <section className="bg-ivory py-24 lg:py-32 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-14 items-start">
          <Reveal className="lg:col-span-5">
            <div className="overline mb-6">Why it matters early</div>
            <h2 className="font-serif font-light text-4xl lg:text-5xl leading-[1.05] text-espresso">The nervous system develops fast in childhood.</h2>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-7 space-y-6 text-lg font-light leading-relaxed text-espressosoft/85">
            <p>From the first days of life, a child's brain and spinal cord are learning how to sense and control the body. Movement, posture and the balance between the 'go' and 'rest' sides of the nervous system all shape how a child feels and functions.</p>
            <p>Because so much of a child's day — sleep, digestion, attention, mood, coordination — is governed by the nervous system, supporting how it senses and regulates the body can have a wide, gentle influence.</p>
            <p>Our pediatric care uses very light contact appropriate to a child's size and age. It looks nothing like adult adjusting — think gentle pressure, no force, and a parent involved throughout.</p>
          </Reveal>
        </div>
      </section>

      {/* Supports */}
      <section className="bg-linen py-24 lg:py-32 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <div className="overline mb-5">What families come to us for</div>
            <h2 className="font-serif font-light text-4xl lg:text-5xl text-espresso max-w-2xl">Gentle support for everyday challenges.</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10 mt-14">
            {SUPPORTS.map(([t, d], i) => (
              <Reveal key={t} delay={(i % 3) * 70}>
                <div className="border-t border-espresso/15 pt-6">
                  <h3 className="font-serif text-2xl text-espresso mb-2">{t}</h3>
                  <p className="text-espressosoft/80 font-light leading-relaxed text-[0.95rem]">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Gentle & safe */}
      <section className="bg-espresso text-ivory py-24 lg:py-32 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-5 lg:col-start-2">
            <div className="overflow-hidden rounded-sm aspect-[4/5] max-w-md mx-auto bg-espressosoft">
              <LoopVideo src={resolveImg(IMG.video)} poster={resolveImg(IMG.poster)} className="w-full h-full object-cover"
                label="Gentle infant chiropractic care at Psalm 30:2" testid="pediatric-care-video" />
            </div>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-6">
            <div className="overline !text-brass mb-5">Gentle & family-centered</div>
            <h2 className="font-serif font-light text-4xl lg:text-5xl mb-6">Care built around your child.</h2>
            <p className="text-lg font-light leading-relaxed text-ivory/75 mb-4">Every visit is unhurried, playful and led by your child's comfort. We explain everything, answer your questions, and keep parents involved at every step.</p>
            <p className="text-lg font-light leading-relaxed text-ivory/75">Our goal is simple: support a well-regulated, well-moving nervous system so your child can sleep, focus, digest and play like a kid should.</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-20 lg:py-28 px-6 md:px-10 lg:px-14">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <p className="font-serif font-light text-2xl lg:text-3xl leading-snug text-espresso mb-6">Gentle care that supports how your child grows and feels.</p>
            <p className="text-sm text-taupe max-w-xl mx-auto mb-10">This page is educational and is not medical advice, a diagnosis, or a claim to treat any specific disease or condition. Chiropractic care supports nervous-system function and movement; it does not replace your pediatrician. We're glad to coordinate with your child's care team.</p>
            <BookButton label="Book Pediatric Care" variant="primary" track_label="pediatric" testid="pediatric-book-button" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
